/**
 * Vocabulaire métier de TechPlanner : valeurs stockées (anglais, stables)
 * et libellés affichés (français).
 */

export const ROLES = ["admin", "technician"] as const;
export type Role = (typeof ROLES)[number];

export const INTERVENTION_STATUSES = ["planned", "in_progress", "done", "cancelled"] as const;
export type InterventionStatus = (typeof INTERVENTION_STATUSES)[number];

export const STATUS_LABELS: Record<InterventionStatus, string> = {
  planned: "À faire",
  in_progress: "En cours",
  done: "Terminée",
  cancelled: "Annulée"
};

/** Une intervention dans l'un de ces statuts est figée : la régénération d'un contrat n'y touche plus. */
export const FROZEN_STATUSES: readonly InterventionStatus[] = ["done", "cancelled"];

export function isFrozenStatus(status: InterventionStatus): boolean {
  return FROZEN_STATUSES.includes(status);
}

export const PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: "Basse",
  normal: "Normale",
  high: "Haute",
  urgent: "Urgente"
};

export const INTERVENTION_KINDS = ["maintenance", "repair", "installation", "visit", "other"] as const;
export type InterventionKind = (typeof INTERVENTION_KINDS)[number];

export const KIND_LABELS: Record<InterventionKind, string> = {
  maintenance: "Maintenance",
  repair: "Dépannage",
  installation: "Installation",
  visit: "Visite",
  other: "Autre"
};

export const EQUIPMENT_KINDS = ["milking_robot", "compressor", "other"] as const;
export type EquipmentKind = (typeof EQUIPMENT_KINDS)[number];

export const EQUIPMENT_KIND_LABELS: Record<EquipmentKind, string> = {
  milking_robot: "Robot de traite",
  compressor: "Compresseur",
  other: "Autre équipement"
};

export const KIT_MODELS = {
  gemini: { label: "Gemini", count: 8 },
  gemini_up: { label: "Gemini UP", count: 6 }
} as const;
export type KitModel = keyof typeof KIT_MODELS;
export const KIT_MODEL_KEYS = Object.keys(KIT_MODELS) as KitModel[];

/** Bornes des compteurs de fréquence (mois / jours) d'un équipement ou d'un contrat. */
export const FREQUENCY_MAX_MONTHS = 36;
export const FREQUENCY_MAX_DAYS = 365;

export const CONTRACT_STATUSES = ["active", "ended"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Nombre maximal de passages générés pour un contrat (garde-fou). */
export const MAX_CONTRACT_OCCURRENCES = 60;

/** « Tous les 4 mois », « Tous les 3 mois et 15 jours », « Tous les 15 jours », « Tous les mois ». */
export function formatFrequency(months: number, days = 0): string {
  if (!Number.isInteger(months)) {
    // Anciennes fréquences en demi-mois (3,5 mois).
    return `Tous les ${Math.trunc(months)} mois ½${days ? ` et ${days} jour${days > 1 ? "s" : ""}` : ""}`;
  }
  const parts: string[] = [];
  if (months) parts.push(months === 1 ? "mois" : `${months} mois`);
  if (days) parts.push(days === 1 && !months ? "jour" : `${days} jour${days > 1 ? "s" : ""}`);
  if (!parts.length) return "Pas d'entretien périodique";
  return `Tous les ${parts.join(" et ")}`;
}

export function kitLabel(index: number, model: KitModel, startNumber = 1): string {
  const { label, count } = KIT_MODELS[model];
  const start = Math.min(Math.max(1, Math.trunc(startNumber)), count);
  const number = ((index + start - 1) % count) + 1;
  return `${label} · Kit ${number}`;
}
